<template>
  <div class="package-container">
    <!-- 页面头部 -->
    <el-page-header @back="goBack" :content="`套餐管理 - ${productCode}`" />

    <!-- 数据表格 -->
    <el-card class="table-card" shadow="never" style="margin-top: 20px">
      <!-- 顶部按钮 -->
      <div style="margin-bottom: 20px">
        <el-button type="primary" icon="el-icon-plus" @click="handleCreate">
          新增套餐
        </el-button>
      </div>

      <el-table
        v-loading="listLoading"
        :data="packageList"
        border
        fit
        highlight-current-row
        style="width: 100%"
      >
        <!-- ID -->
        <el-table-column
          prop="id"
          label="ID"
          width="80"
          align="center"
        />

        <!-- 套餐编码 -->
        <el-table-column
          prop="package_code"
          label="套餐编码"
          width="150"
          align="center"
        />

        <!-- 套餐名称 -->
        <el-table-column
          prop="package_name"
          label="套餐名称"
          width="200"
          align="center"
          show-overflow-tooltip
        />

        <!-- 价格 -->
        <el-table-column
          prop="price"
          label="价格(元)"
          width="120"
          align="center"
        >
          <template slot-scope="{ row }">
            <span>¥{{ formatMoney(row.price) }}</span>
          </template>
        </el-table-column>

        <!-- 有效期 -->
        <el-table-column
          prop="duration"
          label="有效期(天)"
          width="120"
          align="center"
        />

        <!-- 流量额度 -->
        <el-table-column
          prop="data_amount"
          label="流量额度"
          width="120"
          align="center"
        />

        <!-- 收款商户 -->
        <el-table-column
          prop="merchant_name"
          label="收款商户"
          width="180"
          align="center"
          show-overflow-tooltip
        >
          <template slot-scope="{ row }">
            <span v-if="row.merchant_name">{{ row.merchant_name }}</span>
            <span v-else style="color: #909399">使用商品配置</span>
          </template>
        </el-table-column>

        <!-- 状态 -->
        <el-table-column
          prop="status"
          label="状态"
          width="100"
          align="center"
        >
          <template slot-scope="{ row }">
            <el-tag :type="getProductStatusTagType(row.status)" size="small">
              {{ getProductStatusText(row.status) }}
            </el-tag>
          </template>
        </el-table-column>

        <!-- 创建时间 -->
        <el-table-column
          prop="created_at"
          label="创建时间"
          width="180"
          align="center"
        >
          <template slot-scope="{ row }">
            {{ formatDate(row.created_at) }}
          </template>
        </el-table-column>

        <!-- 操作列 -->
        <el-table-column
          label="操作"
          width="200"
          align="center"
          fixed="right"
        >
          <template slot-scope="{ row }">
            <el-button type="text" size="small" @click="handleEdit(row)">
              编辑
            </el-button>
            <el-button
              type="text"
              size="small"
              @click="handleToggleStatus(row)"
            >
              {{ row.status === 1 ? '下架' : '上架' }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-container">
        <el-pagination
          :current-page="listQuery.page"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="listQuery.page_size"
          :total="total"
          background
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>

    <!-- 新增/编辑套餐弹窗 -->
    <package-dialog
      ref="packageDialog"
      :product-code="productCode"
      @success="fetchPackageList"
    />
  </div>
</template>

<script>
import { getPackageList, updatePackageStatus } from '@/api/iccid-package'
import PackageDialog from './components/PackageDialog'
import {
  getProductStatusText,
  getProductStatusTagType,
  formatMoney,
  formatDate
} from '@/utils/iccid-formatter'

export default {
  name: 'IccidPackageList',
  components: {
    PackageDialog
  },
  data() {
    return {
      productCode: '',
      packageList: [],
      total: 0,
      listLoading: false,
      listQuery: {
        page: 1,
        page_size: 20,
        status: -1  // -1表示显示全部状态（上架+下架）
      }
    }
  },
  created() {
    // 从路由参数获取商品编码
    this.productCode = this.$route.params.productCode
    if (!this.productCode) {
      this.$message.error('缺少商品编码参数')
      this.goBack()
      return
    }
    this.fetchPackageList()
  },
  methods: {
    // 格式化函数
    formatMoney,
    formatDate,
    getProductStatusText,
    getProductStatusTagType,

    // 获取套餐列表
    async fetchPackageList() {
      this.listLoading = true
      try {
        const params = {
          ...this.listQuery,
          product_code: this.productCode
        }
        const { data } = await getPackageList(params)
        this.packageList = data.list || []
        this.total = data.total || 0
      } catch (error) {
        this.$message.error('获取套餐列表失败')
      } finally {
        this.listLoading = false
      }
    },

    // 分页
    handleSizeChange(val) {
      this.listQuery.page_size = val
      this.fetchPackageList()
    },
    handleCurrentChange(val) {
      this.listQuery.page = val
      this.fetchPackageList()
    },

    // 新增套餐
    handleCreate() {
      this.$refs.packageDialog.open()
    },

    // 编辑套餐
    handleEdit(row) {
      this.$refs.packageDialog.open(row)
    },

    // 切换状态
    handleToggleStatus(row) {
      const status = row.status === 1 ? 0 : 1
      const statusText = status === 1 ? '上架' : '下架'

      this.$confirm(`确认${statusText}该套餐吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        return updatePackageStatus({
          id: row.id,
          package_code: row.package_code,
          status: status
        })
      }).then(() => {
        this.$message.success(`${statusText}成功`)
        this.fetchPackageList()
      }).catch(() => {})
    },

    // 返回商品列表
    goBack() {
      this.$router.push({ name: 'IccidProductList' })
    }
  }
}
</script>

<style scoped>
.package-container {
  padding: 20px;
}

.table-card {
  margin-bottom: 20px;
}

.pagination-container {
  margin-top: 20px;
  text-align: right;
}
</style>
